import { ArrowLeft } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { AnyRouter, LinkComponentProps, RegisteredRouter } from '@tanstack/react-router';

const BACK_LINK_CLASS_NAME =
  'inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors';

type ClientDetailBackLinkProps<
  TRouter extends AnyRouter = RegisteredRouter,
  TFrom extends string = string,
  TTo extends string | undefined = undefined,
  TMaskFrom extends string = TFrom,
  TMaskTo extends string = '',
> = LinkComponentProps<'a', TRouter, TFrom, TTo, TMaskFrom, TMaskTo> & { label: string };

/**
 * Shared "back to offer" affordance for the client media-unit detail routes
 * (`/offers/:offerId/units/:unitId` and `/public/offers/:token/units/:unitId`) — the single
 * place the shared `text-[13px]` back-link style and `ArrowLeft` glyph live, instead of each
 * route (and each conditional branch within a route) repeating its own copy
 * (ds-arbitrary-value / qual-dry-violation fix, CU-86cbde3ae). Mirrors `RowDetailLink`'s generic
 * signature so callers keep full `to`/`params` type-checking against their own route.
 */
export function ClientDetailBackLink<
  TRouter extends AnyRouter = RegisteredRouter,
  const TFrom extends string = string,
  const TTo extends string | undefined = undefined,
  const TMaskFrom extends string = TFrom,
  const TMaskTo extends string = '',
>({ label, ...linkProps }: ClientDetailBackLinkProps<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>) {
  // Same cross-generic forwarding limitation documented in RowDetailLink
  // (microsoft/TypeScript#28884) — the checked public contract callers see is
  // ClientDetailBackLinkProps above, which is the real generic LinkComponentProps shape.
  const linkComponentProps = linkProps as unknown as LinkComponentProps<'a'>;
  return (
    <Link {...linkComponentProps} className={BACK_LINK_CLASS_NAME} data-testid="back-link">
      <ArrowLeft className="h-4 w-4" />
      {label}
    </Link>
  );
}
